import React, { useState, useEffect } from 'react';
import { AlertTriangle, TrendingUp, Activity, Clock, MapPin, Droplet, Lock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { predictiveAnalyticsService } from '../../services/predictiveAnalyticsService';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const PredictiveAnalyticsDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [predictions, setPredictions] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (!authLoading) {
      if (user?.id) {
        loadCompanyAndPredictions();
      } else {
        setLoading(false);
      }
    }
  }, [user?.id, authLoading]);

  const loadCompanyAndPredictions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user's company ID
      const { data: employeeData } = await supabase
        ?.from('company_employees')
        ?.select('company_id')
        ?.eq('user_id', user?.id)
        ?.single();

      if (!employeeData?.company_id) {
        setError('User not associated with a company');
        return;
      }

      setCompanyId(employeeData?.company_id);

      // Load predictions
      const predictionsData = await predictiveAnalyticsService?.getCombinedPredictions(employeeData?.company_id);
      setPredictions(predictionsData);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading predictions:', error);
      
      // Provide user-friendly error messages
      if (error?.message?.includes('Rate limit')) {
        setError('API rate limit reached. Please wait a moment before refreshing. Predictions are cached for 5 minutes to reduce API calls.');
      } else if (error?.message?.includes('API key')) {
        setError('API configuration issue. Please contact support.');
      } else if (error?.message?.includes('Quota exceeded')) {
        setError('API quota exceeded. Please try again later or contact support.');
      } else {
        setError(error?.message || 'Failed to load predictive analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadCompanyAndPredictions();
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Show loading state while checking authentication
  if (authLoading || (user && loading)) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Activity className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading predictive analytics...</p>
              <p className="text-sm text-gray-500 mt-2">This may take a moment as we analyze your data</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show sign-up prompt for non-authenticated users
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-lg border border-gray-200 shadow-lg p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
              <Lock className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Sign In Required
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Access to Predictive Analytics is available for signed-in users only.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <h3 className="text-xl font-semibold text-blue-900 mb-3">
                Unlock AI-Powered Predictions
              </h3>
              <ul className="text-left space-y-2 text-gray-700 mb-4">
                <li className="flex items-start">
                  <TrendingUp className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Predict compliance violations before they occur</span>
                </li>
                <li className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Forecast pest pressure spikes with AI analysis</span>
                </li>
                <li className="flex items-start">
                  <Activity className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Get proactive intervention recommendations</span>
                </li>
                <li className="flex items-start">
                  <Clock className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Real-time confidence scores and risk assessments</span>
                </li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/login')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate('/login')}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg"
              >
                Sign Up Now
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              Already have an account? <button onClick={() => navigate('/login')} className="text-blue-600 hover:underline font-medium">Sign in here</button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900 mb-2">Error Loading Predictions</h3>
                <p className="text-red-700 mb-4">{error}</p>
                <Button
                  onClick={handleRefresh}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Predictive Analytics</h1>
            <p className="text-gray-600 mt-1">
              AI-powered predictions for compliance violations and pest pressure spikes
            </p>
            {lastUpdated && (
              <p className="text-sm text-gray-500 mt-1">
                Last updated: {lastUpdated?.toLocaleString()}
              </p>
            )}
          </div>
          <Button onClick={handleRefresh} className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Refresh Predictions
          </Button>
        </div>

        {/* Confidence Scores */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Compliance Prediction Confidence</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {(predictions?.compliance?.confidence_score * 100)?.toFixed(1)}%
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pest Pressure Prediction Confidence</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {(predictions?.pestPressure?.confidence_score * 100)?.toFixed(1)}%
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Compliance Violations Predictions */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                Compliance Violation Predictions
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {predictions?.compliance?.analysis_summary}
              </p>
            </div>

            <div className="p-4 space-y-4">
              {predictions?.compliance?.predictions?.map((prediction, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{prediction?.violation_type}</h3>
                      <p className="text-sm text-gray-600 mt-1">{prediction?.predicted_timeframe}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRiskColor(prediction?.risk_level)}`}>
                      {prediction?.risk_level?.toUpperCase()}
                    </span>
                  </div>

                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">Probability</span>
                      <span className="font-semibold text-gray-900">
                        {(prediction?.probability * 100)?.toFixed(1)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-orange-600 h-2 rounded-full"
                        style={{ width: `${prediction?.probability * 100}%` }}
                      />
                    </div>
                  </div>

                  {prediction?.contributing_factors?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-700 mb-1">Contributing Factors:</p>
                      <div className="flex flex-wrap gap-1">
                        {prediction?.contributing_factors?.map((factor, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {factor}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {prediction?.affected_locations?.length > 0 && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                      <MapPin className="w-3 h-3" />
                      <span>{prediction?.affected_locations?.join(', ')}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Proactive Interventions */}
            <div className="p-4 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">Recommended Proactive Interventions</h3>
              <div className="space-y-2">
                {predictions?.compliance?.proactive_interventions?.map((intervention, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(intervention?.priority)}`}>
                      {intervention?.priority?.toUpperCase()}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{intervention?.action}</p>
                      {intervention?.target_area && (
                        <p className="text-xs text-gray-600 mt-1">Target: {intervention?.target_area}</p>
                      )}
                      {intervention?.estimated_impact && (
                        <p className="text-xs text-gray-600 mt-1">Impact: {intervention?.estimated_impact}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pest Pressure Predictions */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                Pest Pressure Spike Predictions
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {predictions?.pestPressure?.analysis_summary}
              </p>
            </div>

            <div className="p-4 space-y-4">
              {predictions?.pestPressure?.predictions?.map((prediction, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{prediction?.pest_type}</h3>
                      <p className="text-sm text-gray-600 mt-1">{prediction?.predicted_timeframe}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRiskColor(prediction?.risk_level)}`}>
                      {prediction?.risk_level?.toUpperCase()}
                    </span>
                  </div>

                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">Pressure Level</span>
                      <span className="font-semibold text-gray-900">
                        {prediction?.pressure_level}/5
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full"
                        style={{ width: `${(prediction?.pressure_level / 5) * 100}%` }}
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">Probability</span>
                      <span className="font-semibold text-gray-900">
                        {(prediction?.probability * 100)?.toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  {prediction?.environmental_triggers?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-700 mb-1">Environmental Triggers:</p>
                      <div className="flex flex-wrap gap-1">
                        {prediction?.environmental_triggers?.map((trigger, idx) => (
                          <span key={idx} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-1">
                            <Droplet className="w-3 h-3" />
                            {trigger}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {prediction?.affected_locations?.length > 0 && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                      <MapPin className="w-3 h-3" />
                      <span>{prediction?.affected_locations?.join(', ')}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Preventive Treatments */}
            <div className="p-4 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">Recommended Preventive Treatments</h3>
              <div className="space-y-2">
                {predictions?.pestPressure?.preventive_treatments?.map((treatment, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(treatment?.priority)}`}>
                      {treatment?.priority?.toUpperCase()}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{treatment?.treatment}</p>
                      <p className="text-xs text-gray-600 mt-1">Target: {treatment?.target_pest}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-600">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {treatment?.recommended_timing}
                        </span>
                        {treatment?.expected_effectiveness && (
                          <span className="text-xs font-medium text-green-600">
                            {treatment?.expected_effectiveness}% effective
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Environmental Monitoring */}
            {predictions?.pestPressure?.environmental_monitoring?.length > 0 && (
              <div className="p-4 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">Environmental Monitoring</h3>
                <div className="space-y-2">
                  {predictions?.pestPressure?.environmental_monitoring?.map((monitor, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{monitor?.parameter}</p>
                        {monitor?.current_value && (
                          <p className="text-xs text-gray-600">Current: {monitor?.current_value}</p>
                        )}
                        {monitor?.optimal_range && (
                          <p className="text-xs text-gray-600">Optimal: {monitor?.optimal_range}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-700">{monitor?.risk_assessment}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* High Risk Periods */}
        {predictions?.compliance?.high_risk_periods?.length > 0 && (
          <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-3">High Risk Periods</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {predictions?.compliance?.high_risk_periods?.map((period, index) => (
                <div key={index} className="border border-orange-200 bg-orange-50 rounded-lg p-3">
                  <p className="font-medium text-gray-900">{period?.period}</p>
                  <ul className="mt-2 space-y-1">
                    {period?.risk_factors?.map((factor, idx) => (
                      <li key={idx} className="text-xs text-gray-700 flex items-start gap-1">
                        <span className="text-orange-600 mt-0.5">•</span>
                        <span>{factor}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PredictiveAnalyticsDashboard;
